import {
    useAllTaskListSelector,
    useCompletedChecklistMapSelector,
    useTaskCodeMapSelector
} from "../../../../../store/selectors/task/task.selectors";
import {
    useActiveProjectCalendarsSelector, useChecklistMapSelector,
    useClientIdSelector,
    useCpmMapSelector
} from "../../../../../store/selectors/project.selectors";
import {useProjectMemberListSelector} from "../../../../../store/selectors/team.selectors";
import React, {useMemo, useState} from "react";
import {WorkPackage} from "../../../../../models/responses/work-package.model";
import {useUserGradeSelector} from "../../../../../store/selectors/authorization.selectors";
import {MemberOrganizationEnum} from "../../../../../models/member-organization.enum";
import TaskModel from "../../../../../models/responses/task.model";
import {CpmTaskModel} from "../../../../../models/responses/cpm-task.model";
import {convertIndexToSeconds} from "../../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import moment from "moment/moment";
import TaskStatusModel from "../../../../../models/responses/task-status.model";
import FirebaseUsage from "../../../../../firebase/firebase.usage";
import {COLLECTIONS} from "../../../../../firebase/constants";
import styles from "../WorkPackages.module.scss";
import classNames from "classnames";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCrown} from "@fortawesome/free-solid-svg-icons";
import {Dropdown} from "semantic-ui-react";
import {faPen} from "@fortawesome/pro-solid-svg-icons";
import WorkPackageChecklist from "./WorkPackageChecklist";
import WorkPackageTaskList from "./WorkPackageTaskList";
import NewChecklistModal from "../../ChecklistPage/components/NewChecklistModal";

export default function WorkPackageSingle(props: {wp: WorkPackage}) {
    const {wp} = props;
    const allTasks = useAllTaskListSelector();
    const taskCodeMap = useTaskCodeMapSelector();
    const cpmMap = useCpmMapSelector();
    const calendarMap = useActiveProjectCalendarsSelector();
    const client = useClientIdSelector();
    const projectMembers = useProjectMemberListSelector();
    const [openChecklistModal, setOpenChecklistModal] = useState<boolean>(false);
    const [predecessorToggle, setPredecessorToggle] = useState(false);
    const [successorToggle, setSuccessorToggle] = useState(false);
    const checklistMap = useChecklistMapSelector();
    const completedChecklistMap = useCompletedChecklistMapSelector();
    const [workPackageDescription, setWorkPackageDescription] = useState<string | null>(null);
    const userGrade = useUserGradeSelector();

    const userIsAdmin = userGrade[MemberOrganizationEnum.SUPER_ACTOR]

    const workPackage = useMemo(() => {
        const tasks: TaskModel[] = wp.assignedTaskCodes
            .map(taskCode => allTasks.get(taskCodeMap.get(taskCode)!))
            .filter((task): task is TaskModel => task !== undefined)
        let packageStart: null | number = null;
        let packageEnd: null | number = null;
        let isCritical = false;
        let isCriticalNow = false;
        let isNearCritical = false;
        let isInProgress = false;
        let drivingTaskStart: null | TaskModel = null;
        let drivingTaskEnd: null | TaskModel = null;
        tasks.forEach(task => {
            const cpmTask: CpmTaskModel = cpmMap.get(task.task_id);
            if (!cpmTask) return;
            const taskStart = convertIndexToSeconds(cpmTask.fs ? cpmTask.fs : cpmTask.es, cpmTask.cal_id, calendarMap);
            const taskEnd = convertIndexToSeconds(cpmTask.ff ? cpmTask.ff : cpmTask.ef, cpmTask.cal_id, calendarMap);
            const workingHours = calendarMap.get(`whd:${cpmTask.cal_id}`);
            if (packageStart === null || taskStart < packageStart) {
                packageStart = taskStart;
                drivingTaskStart = task;
            }
            if (packageEnd === null || taskEnd > packageEnd) {
                packageEnd = taskEnd;
                drivingTaskEnd = task;
            }
            if (cpmTask.float === 0 || (cpmTask.float && cpmTask.float < workingHours)) {
                isCritical = true;
                if (convertIndexToSeconds(cpmTask.ls, cpmTask.cal_id, calendarMap) <=
                    (moment(new Date()).add(1, 'days').toDate().getTime() / 1000)) {
                    isCriticalNow = true;
                }
            } else if (cpmTask.float && cpmTask.float < workingHours * 2) {
                isNearCritical = true;
            }
            if (task.status !== TaskStatusModel.NOT_STARTED && task.status !== TaskStatusModel.BLOCK) {
                isInProgress = true;
            }
        });

        const checklist = checklistMap.get(wp.checklistId);
        let incompleteChecklistItems: any = [];
        if (!checklist) {
            return {
                ...wp,
                tasks: tasks
                    .sort((a, b) => {
                        const cpmTaskA: CpmTaskModel | undefined = cpmMap.get(a.task_id);
                        const cpmTaskB: CpmTaskModel | undefined = cpmMap.get(b.task_id);
                        if (!cpmTaskA || !cpmTaskB) return 0;
                        const aStart = convertIndexToSeconds(cpmTaskA.ls, cpmTaskA.cal_id, calendarMap);
                        const bStart = convertIndexToSeconds(cpmTaskB.ls, cpmTaskB.cal_id, calendarMap);
                        return aStart - bStart;
                    }),
                checklist: [],
                packageStart,
                packageEnd,
                isCritical,
                isCriticalNow,
                isNearCritical,
                drivingTaskStart,
                drivingTaskEnd,
                isInProgress
            }
        }

        let earliestDueDate: null | number = null;
        let overdueItemsCount = 0;
        checklist.items.forEach((checklistItem: any) => {
            const completionRecord = completedChecklistMap.get(`${wp.workPackageId}:${checklistItem.checklistItemId}`);
            const cpmTask = drivingTaskStart && drivingTaskEnd ?
                checklistItem.tMinusDays <= 0 ? cpmMap.get(drivingTaskStart.task_id) : cpmMap.get(drivingTaskEnd.task_id) : null;
            const workingHours = cpmTask ? calendarMap.get(`whd:${cpmTask.cal_id}`) : 8;
            const dueDate = checklistItem.tMinusDays > 0 ?
                convertIndexToSeconds((cpmTask.ff ? cpmTask.ff : cpmTask.ef) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap) :
                convertIndexToSeconds((cpmTask.fs ? cpmTask.fs : cpmTask.es) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap)
            if (!completionRecord && dueDate && dueDate < (new Date().getTime() / 1000)) {
                overdueItemsCount += 1;
            }
            if (earliestDueDate === null || dueDate < earliestDueDate) {
                earliestDueDate = dueDate;
            }
            incompleteChecklistItems.push({
                ...checklistItem,
                type: 'workPackage',
                due: dueDate,
                completionRecord: completionRecord,
            })
        });

        return {
            ...wp,
            tasks: tasks
                .sort((a, b) => {
                    const cpmTaskA: CpmTaskModel | undefined = cpmMap.get(a.task_id);
                    const cpmTaskB: CpmTaskModel | undefined = cpmMap.get(b.task_id);
                    if (!cpmTaskA || !cpmTaskB) return 0;
                    const aStart = convertIndexToSeconds(cpmTaskA.ls, cpmTaskA.cal_id, calendarMap);
                    const bStart = convertIndexToSeconds(cpmTaskB.ls, cpmTaskB.cal_id, calendarMap);
                    return aStart - bStart;
                }),
            checklist: incompleteChecklistItems,
            packageStart,
            packageEnd,
            isCritical,
            isCriticalNow,
            isNearCritical,
            drivingTaskStart,
            drivingTaskEnd,
            isInProgress,
            earliestDueDate,
            overdueChecklistItemsCount: overdueItemsCount
        }
    }, [wp]);

    const updateWorkPackageDescription = (workPackageId: string) => {
        FirebaseUsage.updateDoc(COLLECTIONS.WORK_PACKAGES, workPackageId, {
            workPackageDescription: workPackageDescription
        }).then(() => {
            setWorkPackageDescription(null);
        })
    }

    const changeWorkPackageOwner = async (workPackageId: string, ownerEmail: string) => {
        await FirebaseUsage.updateDoc(COLLECTIONS.WORK_PACKAGES, workPackageId, {
            workPackageOwner: ownerEmail
        })
    }

    const memberList = useMemo(() => {
        return projectMembers.filter(member =>
            member.grade !== MemberOrganizationEnum.GROUPS && !member.leftProject
        )
            .map(member => ({
                key: member.memberId,
                text: member.userEmail,
                value: member.userEmail
            }));
    }, [projectMembers]);

    const workPackageTag = (wp: WorkPackage) => {
        if (!wp.checklistId) {
            return (
                <div className={styles.NoChecklistTag}>
                    No checklist assigned
                </div>
            )
        }
        if (wp.overdueChecklistItemsCount && wp.overdueChecklistItemsCount > 0) {
            return (
                <div className={classNames(styles.OverdueTag, styles.Blinking)}>
                    {wp.overdueChecklistItemsCount} overdue action{wp.overdueChecklistItemsCount > 1 ? 's' : ''}
                </div>
            )
        } else if (wp.completedChecklist && wp.checklist && wp.completedChecklist.length >= wp.checklist.length) {
            if (wp.isInProgress) {
                return (
                    <div className={styles.InProgressTag}>
                        In progress
                    </div>
                )
            } else {
                return (
                    <div className={styles.ReadyTag}>
                        Ready to start
                    </div>
                )
            }
        } else {
            return (
                <div className={styles.NotReadyTag}>
                    Actions due in {wp.earliestDueDate ? moment.unix(wp.earliestDueDate).fromNow(true) : 'N/A'}
                </div>
            );
        }
    }

    const workPackageElement = (wp: WorkPackage | undefined) => wp && wp.tasks && wp.tasks.length > 0 ? (
        <div className={styles.WorkPackage}
             style={{backgroundColor: '#f0f0f0', cursor: 'auto'}}
             key={wp.workPackageId}
        >
            <div className={styles.InfoPane}>
                <div className={styles.Titles}>
                    {wp.workPackageName} ({wp.workPackageRef})
                    {workPackageTag(wp)}
                </div>
                <div className={styles.Details}>
                    <div className={styles.DetailItem}>
                        <span className={styles.DetailLabel}>{
                            wp.packageStart ? moment(new Date(wp.packageStart * 1000)).format('DD-MMM-YY') : 'N/A'}
                        </span>
                    </div>
                    <div className={styles.DetailItem}>
                        <span className={styles.DetailLabel}>{
                            wp.packageEnd ? moment(new Date(wp.packageEnd * 1000)).format('DD-MMM-YY') : 'N/A'}
                        </span>
                    </div>
                    <div className={styles.DetailItem}>
                        <div className={wp.isCritical ? wp.isCriticalNow ?
                                styles.CriticalTag : styles.CriticalPathTag :
                            wp.isNearCritical ? styles.NearCriticalTag :
                                styles.NonCriticalTag
                        }>
                            {wp.isCritical ? wp.isCriticalNow ?  'Critical' : 'Critical Path' :
                                wp.isNearCritical ? 'Near Critical' : ''}
                        </div>
                    </div>
                </div>
            </div>
            {!wp.workPackageDescription || workPackageDescription !== null ?
                <textarea
                    placeholder={workPackageDescription ? undefined : 'Add work package description...'}
                    autoFocus
                    value={workPackageDescription || ''}
                    className={styles.WorkPackageDescriptionInput}
                    onChange={(e) => setWorkPackageDescription(e.target.value)}
                    onBlur={() => updateWorkPackageDescription(wp.workPackageId)}
                /> :
                <div className={styles.WorkPackageDescription}>
                    <p>{wp.workPackageDescription}{userIsAdmin &&
                        <FontAwesomeIcon
                            icon={faPen}
                            className={styles.EditPen}
                            onClick={() => setWorkPackageDescription(wp.workPackageDescription || '')}
                        />}</p>
                </div>}
            {!wp.workPackageOwner ?
                    <Dropdown trigger={
                        <button
                            className={styles.ActionButton}
                        >
                            Assign work package owner
                        </button>}
                              options={memberList}
                              icon={null}
                              scrolling
                              onChange={(_, data) =>
                                  data.value && changeWorkPackageOwner(wp.workPackageId, data.value.toString())}
                    />
                    :
                    <div className={styles.WorkPackageOwner}>
                        <FontAwesomeIcon icon={faCrown} style={{marginRight: "8px"}} />
                        {wp.workPackageOwner}
                        {userIsAdmin && <Dropdown trigger={
                            <FontAwesomeIcon
                                icon={faPen}
                                className={styles.EditPen} />}
                                                  options={memberList}
                                                  icon={null}
                                                  scrolling
                                                  onChange={(_, data) =>
                                                      data.value && changeWorkPackageOwner(wp.workPackageId, data.value.toString())}
                        />}
                    </div>}
            {wp.checklist && wp.checklist.length > 0 ?
                <WorkPackageChecklist
                    checklistItems={wp.checklist ? wp.checklist : []}
                    workPackage={wp}
                    infoTag={workPackageTag(wp)}
                /> :
                <button
                    className={styles.ActionButton}
                    onClick={() => setOpenChecklistModal(true)}
                >
                    Assign pre-flight checklist
                </button>}
                <WorkPackageTaskList
                    tasks={wp.tasks ? wp.tasks : []}
                    workPackage={wp}
                    predecessorToggle={predecessorToggle}
                    successorToggle={successorToggle}
                    setPredecessorToggle={setPredecessorToggle}
                    setSuccessorToggle={setSuccessorToggle}
                    navigateToTask={() => {}}
                    selectNextTask={() => {}}
                />
            <div></div>
        </div>
    ) :  null;

    return (
        <div className={styles.WorkPackageContainer}
             style={{
                 maxHeight: 'calc(100vh - 160px)',
                 overflowY: 'auto',
                 paddingLeft: '20px',
                 paddingRight: '20px',
                 paddingBottom: '10px',
        }}
        >
            <div className={styles.WorkPackagesHeader}>
                <div className={styles.HeaderTitle}>Name</div>
                <div className={styles.RightHeaders}>
                    <div className={styles.RightHeader}>Start</div>
                    <div className={styles.RightHeader}>Finish</div>
                    <div className={styles.RightHeader}>Criticality</div>
                </div>
            </div>
            {workPackageElement(workPackage)}
            {openChecklistModal &&
                <NewChecklistModal
                    isOpen={openChecklistModal}
                    setOpen={setOpenChecklistModal}
                    clientId={client || ''}
                    workPackageId={workPackage.workPackageId}
                    taskId={null}
                    activeChecklistId={workPackage.checklistId || null}
                />}
        </div>
    );
}